import { ConsultNoteStatus, ContentType } from '@heidi/schema/ml-scribe';

import { type NoteEditorSurface, type NoteEditorType } from '../../../session-page/host';

/**
 * The JSON-cohort gate: a completed JSON note takes the block editor ahead of
 * every flag. Shared by `NoteTabPane` and the slim-surface engine selector so
 * the two can't drift.
 *
 * A completed JSON note is data, not cohort. The onboarding
 * `template_editor_version` pin governs which editor NEW notes are generated
 * into (useResolvedEditorVersion); it cannot retroactively convert stored
 * JSON. Flipping a user to markdown takes effect on their next generation —
 * until then the persisted `content_type` keeps rendering vJSON.
 */
export function isCompletedJsonNote(
  consultNote:
    | { content_type?: ContentType | null; status?: ConsultNoteStatus | null }
    | null
    | undefined
): boolean {
  return (
    consultNote?.content_type === ContentType.JSON &&
    consultNote?.status === ConsultNoteStatus.COMPLETED
  );
}

/** Editor-selection flags, in the priority order `NoteEditorSwitch` evaluates them. */
export type NoteEditorFlags = {
  shouldUseJSONBlockEditor: boolean | undefined;
  isMarkdownNativeEditorEnabled: boolean;
  enableBlockEditorV1: boolean | undefined;
  enableBlockEditorMarkdown: boolean | undefined;
  richTextEditorEnabled: boolean | undefined;
};

/**
 * Single source of truth for which editor the user sees. Branch order mirrors
 * `NoteEditorSwitch` so the resolved kind always matches the rendered editor.
 * Both the Snowplow usage event and the Datadog latency baseline
 * (`resolveNoteEditorVariant`) derive from this — keep them in lockstep.
 */
export function resolveNoteEditorType({
  shouldUseJSONBlockEditor,
  isMarkdownNativeEditorEnabled,
  enableBlockEditorV1,
  enableBlockEditorMarkdown,
  richTextEditorEnabled,
}: NoteEditorFlags): NoteEditorType {
  if (shouldUseJSONBlockEditor) return 'json';
  if (isMarkdownNativeEditorEnabled) return 'markdown_native';
  if (enableBlockEditorV1) return 'block_v1';
  if (enableBlockEditorMarkdown) return 'block_markdown';
  if (richTextEditorEnabled) return 'rich_text';
  return 'textarea';
}

/**
 * The routes that keep a built-but-unrendered vMarkdown fallback (legacy
 * rich-text / textarea, vJSON — see `resolveEditorInstanceNeeds`) must not
 * count that hidden engine's failures against the arm actually rendered.
 * Shares `resolveNoteEditorType` with the denominator so the two can't drift.
 */
export function resolveNoteEditorSurface(
  renderedType: NoteEditorType,
  constructedType: NoteEditorType
): NoteEditorSurface {
  return renderedType === constructedType ? 'note' : 'note_offscreen';
}
